import React, { useState, useEffect, useRef, Fragment } from 'react'
import { Formik, Form } from 'formik'
import { useDrag, useDrop } from 'react-dnd'
import classNames from 'classnames'
import {
  useErrorService,
  useNotificationService,
  ABSpinner,
} from '@abroad/components'
import { ExperienceInputField } from '../form'
import ExperienceCheckbox from '../form/ExperienceCheckbox'
import ExperienceDeleteIcon from './ExperienceDeleteIcon'
import ExperienceButton from './ExperienceButton'
import ExperienceConfirmModal from './ExperienceConfirmModal'
import API from '../../utils/API'
import * as Yup from 'yup'
import {
  closeInlineEditorAfterFlush,
  queueAutosaveOnBlur,
  runQueuedFormikSave,
  FormikDirtyReporter,
} from './questAutosave'

const NON_EDITABLE_QUESTION_COUNT = 4
const MAX_EXTRA_QUESTIONS = 3
const INTEREST_QUESTION_DND = 'INTEREST_EXTRA_QUESTION'

const truncateLabel = (text) => {
  const t = String(text || '').trim()
  if (t.length > 72) return `${t.slice(0, 69)}\u2026`
  return t
}

const LOCKED_DISPLAY = [
  { title: 'Full name', hint: 'Collected from every inquiry', required: true },
  { title: 'Email', hint: 'Collected from every inquiry', required: true },
  {
    title: 'Referral name',
    hint: 'Who should we thank for the introduction',
    required: false,
  },
  {
    title: 'LinkedIn / website',
    hint: 'A public profile or site, if they have one',
    required: false,
  },
  {
    title: 'Occupancy preference',
    hint: 'Single or double, shown with the matching price',
    required: true,
    uiOnly: true,
  },
]

const defaultLockedQuestions = () => [
  {
    description: '',
    question: 'Full Name',
    answerType: 'text',
    isEditable: false,
  },
  {
    description: '',
    question: 'Email',
    answerType: 'email',
    isEditable: false,
  },
  {
    description: '',
    question: 'Referral Name',
    answerType: 'text',
    isEditable: true,
  },
  {
    description: '',
    question: 'LinkedIn or Website (optional)',
    answerType: 'url',
    isEditable: false,
  },
]

const defaultExtraQuestions = () => [
  {
    description: "What's Calling You",
    question:
      'What inspired you to apply for this experience right now? What are you hoping to explore or learn from this experience? How would you describe where you are in your personal and/or leadership journey today?',
    answerType: 'textarea',
    isEditable: true,
    isRequired: true,
  },
  {
    description: 'How You Move in Community',
    question:
      'How do you typically show up in group settings? Are you comfortable with group reflections?',
    answerType: 'textarea',
    isEditable: true,
    isRequired: true,
  },
  {
    description: 'Your Relationship to New Environments',
    question:
      'What has been your experience traveling to places that are unfamiliar or culturally different from your day-to-day life?',
    answerType: 'textarea',
    isEditable: true,
    isRequired: true,
  },
]

const defaultValues = {
  expressInterestForm: {
    inquiryButtonName: 'Apply Now',
    questions: [...defaultLockedQuestions(), ...defaultExtraQuestions()],
  },
}

const validationSchema = Yup.object().shape({
  expressInterestForm: Yup.object().shape({
    inquiryButtonName: Yup.string()
      .max(25, 'Maximum 25 characters allowed')
      .required('Button name is required'),
    questions: Yup.array()
      .of(
        Yup.object().shape({
          question: Yup.string(),
          description: Yup.string(),
        }),
      )
      .min(1, 'At least one question is required'),
  }),
})

const extraQuestionSchema = Yup.object().shape({
  description: Yup.string().trim().required('Title is required'),
  question: Yup.string().trim().required('Question text is required'),
  isRequired: Yup.boolean(),
})

const emptyExtraQuestion = () => ({
  description: '',
  question: '',
  answerType: 'textarea',
  isEditable: true,
  isRequired: true,
})

const hasExtraQuestionContent = (question) =>
  !!(question?.description?.trim() || question?.question?.trim())

const mapApiQuestion = (q, index) => ({
  id: q._id,
  description:
    q.description ||
    defaultValues.expressInterestForm.questions[index]?.description ||
    '',
  answerType: q.answerType || 'text',
  question: q.question || '',
  isEditable:
    q.isEditable !== undefined
      ? q.isEditable
      : defaultValues.expressInterestForm.questions[index]?.isEditable,
  isRequired:
    q.isRequired !== undefined
      ? q.isRequired
      : defaultValues.expressInterestForm.questions[index]?.isRequired !==
        false,
})

const parseFormFromApi = (data) => {
  const apiQuestions = data?.questions || []
  const locked = defaultLockedQuestions().map((fallback, index) => {
    if (apiQuestions[index]) {
      return mapApiQuestion(apiQuestions[index], index)
    }
    return { ...fallback }
  })

  const extra = apiQuestions
    .slice(NON_EDITABLE_QUESTION_COUNT)
    .map((q, index) => mapApiQuestion(q, index + NON_EDITABLE_QUESTION_COUNT))
    .filter((q) => q.question?.trim() || q.description?.trim())

  return {
    expressInterestForm: {
      inquiryButtonName: data.inquiryButtonName || 'Express Interest / Enroll',
      questions: [...locked, ...extra],
    },
  }
}

const InterestQuestionRow = ({
  question,
  index,
  isEditing,
  onEdit,
  onDelete,
  moveItem,
  onDragEnd,
  disabled,
}) => {
  const ref = useRef(null)

  const [{ handlerId }, drop] = useDrop({
    accept: INTEREST_QUESTION_DND,
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() }
    },
    hover(item, monitor) {
      if (!ref.current || disabled) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: INTEREST_QUESTION_DND,
    item: () => ({ index }),
    canDrag: !disabled,
    end() {
      if (!disabled && typeof onDragEnd === 'function') {
        onDragEnd()
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  const isRequired = question.isRequired !== false
  const title = question.description?.trim() || 'Untitled question'
  const subtitle = question.question?.trim()

  const handleActionClick = (e, action) => {
    e.stopPropagation()
    action()
  }

  return (
    <div
      ref={ref}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      className={classNames('experience-lrow', {
        'is-editing': isEditing,
        'is-dragging': isDragging,
      })}
      data-handler-id={handlerId}
      role='button'
      tabIndex={0}
      onClick={() => onEdit(index)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onEdit(index)
        }
      }}>
      <span className='experience-grab' title='Drag to reorder'>
        ⋮⋮
      </span>
      <span className='experience-lrow-copy'>
        <b>{title}</b>
        {subtitle ? <small>{subtitle}</small> : null}
      </span>
      <span className={`experience-req${isRequired ? ' on' : ''}`}>
        {isRequired ? 'Required' : 'Optional'}
      </span>
      <button
        type='button'
        className='experience-x'
        title='Delete'
        aria-label='Delete'
        disabled={disabled}
        onClick={(e) => handleActionClick(e, () => onDelete(index))}>
        <ExperienceDeleteIcon />
      </button>
    </div>
  )
}

const ExtraQuestionInlineEditor = ({ initial, onAutosave }) => {
  const valuesRef = useRef(initial)

  return (
    <div className='experience-add-form'>
      <Formik
        initialValues={initial}
        enableReinitialize
        validationSchema={extraQuestionSchema}
        validateOnChange={false}
        validateOnBlur={true}
        onSubmit={async (values, { resetForm }) => {
          await runQueuedFormikSave({
            values,
            valuesRef,
            save: (latest) => onAutosave(latest),
            resetForm,
          })
        }}>
        {(formik) => {
          const { handleSubmit, values, setFieldValue, submitCount } = formik
          valuesRef.current = formik.values
          const fieldBlur = () => queueAutosaveOnBlur(formik)
          const autosaveAfterChange = () => {
            window.setTimeout(() => queueAutosaveOnBlur(formik), 0)
          }
          return (
            <form onSubmit={handleSubmit}>
              <div className='experience-faq-editor'>
                <div className='experience-f'>
                  <ExperienceInputField
                    name='description'
                    label='Title'
                    placeholder='Add title'
                    capitalizeFirstLetter
                    showErrorMessage={submitCount > 0}
                    displayFlex={false}
                    formGroupProps={{ className: 'mb-0' }}
                    onBlur={fieldBlur}
                  />
                  <p className='experience-f-hint'>
                    The heading guests see, e.g. What&apos;s Calling You.
                  </p>
                </div>
                <div className='experience-f mt-12px'>
                  <ExperienceInputField
                    name='question'
                    label='Question'
                    placeholder='Add the question they answer'
                    as='textarea'
                    rows={3}
                    capitalizeFirstLetter
                    showErrorMessage={submitCount > 0}
                    displayFlex={false}
                    formGroupProps={{ className: 'mb-0' }}
                    onBlur={fieldBlur}
                  />
                </div>
                <ExperienceCheckbox
                  name='isRequired'
                  checked={!!values.isRequired}
                  label='Required'
                  className='mt-3'
                  onChange={(e) => {
                    setFieldValue('isRequired', e.target.checked)
                    autosaveAfterChange()
                  }}
                />
              </div>
            </form>
          )
        }}
      </Formik>
    </div>
  )
}

const ExpressInterestForm = ({ questDetail, onCompletenessChange }) => {
  const [initialValues, setInitialValues] = useState(defaultValues)
  const [isLoading, setIsLoading] = useState(true)
  const [customFormId, setCustomFormId] = useState(null)
  const [isFormSubmitting, setIsFormSubmitting] = useState(false)
  // Tracks last successful save; kept for future dirty-check / rollback.
  // eslint-disable-next-line no-unused-vars
  const [lastSavedFormData, setLastSavedFormData] = useState({})
  const [buttonLabelShowError, setButtonLabelShowError] = useState(false)
  const [editingExtraIndex, setEditingExtraIndex] = useState(null)
  const [extraDraft, setExtraDraft] = useState(emptyExtraQuestion())
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    index: null,
    label: '',
  })
  const deleteActionRef = useRef(null)
  const Error = useErrorService()
  const Notification = useNotificationService()
  const formikRef = useRef()
  const valuesRef = useRef(initialValues)
  const pendingReorderRef = useRef(null)

  const questId = questDetail?._id

  const reportCompleteness = (formData) => {
    if (typeof onCompletenessChange !== 'function') return
    const questions = formData?.expressInterestForm?.questions || []
    onCompletenessChange({
      expressInterestForm: { questions },
    })
  }

  useEffect(() => {
    const fetchExpressInterestFormData = async () => {
      if (!questId) {
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        const response = await API.ClientAPI.quests.getCustomForm(
          questId,
          'questInquiry',
        )
        const { data } = response

        if (data && data.type === 'questInquiry') {
          setCustomFormId(data._id || data.id)
          const parsed = parseFormFromApi(data)
          setInitialValues(parsed)
          setLastSavedFormData(parsed)
          reportCompleteness(parsed)
        } else {
          setInitialValues(defaultValues)
          setLastSavedFormData(defaultValues)
        }
      } catch (e) {
        setInitialValues(defaultValues)
        setLastSavedFormData(defaultValues)
      } finally {
        setIsLoading(false)
      }
    }

    fetchExpressInterestFormData()
  }, [questId])

  const updateFormDetails = async (updatedFormData, options = {}) => {
    if (!questDetail?._id) {
      Notification.showNotification(
        'Please save the experience first',
        'danger',
      )
      return
    }

    setIsFormSubmitting(true)
    try {
      const allQuestions = updatedFormData.expressInterestForm.questions || []
      const locked = allQuestions.slice(0, NON_EDITABLE_QUESTION_COUNT)
      const extra = allQuestions.slice(NON_EDITABLE_QUESTION_COUNT)

      const questionsPayload = [...locked, ...extra]
        .map((q, index) => {
          const defaultQuestion =
            defaultValues.expressInterestForm.questions[index]
          const payload = {
            answerType:
              q.answerType ||
              defaultQuestion?.answerType ||
              (index < NON_EDITABLE_QUESTION_COUNT
                ? ['text', 'email', 'text', 'url'][index]
                : 'textarea'),
            isEditable:
              q.isEditable !== undefined
                ? q.isEditable
                : defaultQuestion?.isEditable ?? index >= 4,
          }

          if (q._id || q.id) {
            payload.id = q._id || q.id
          }
          if (q.question && q.question.trim()) {
            payload.question = q.question.trim()
          }
          if (q.description && q.description.trim()) {
            payload.description = q.description.trim()
          }
          return payload
        })
        .filter((q) => Boolean(q.question || q.description))

      const payload = {
        customFormId: customFormId || null,
        questions: questionsPayload,
      }

      const trimmedButtonName =
        updatedFormData.expressInterestForm.inquiryButtonName &&
        updatedFormData.expressInterestForm.inquiryButtonName.trim()
      if (trimmedButtonName) {
        payload.inquiryButtonName = trimmedButtonName
      }

      if (!questionsPayload.length) {
        setIsFormSubmitting(false)
        Notification.showNotification(
          'Please add at least one question',
          'danger',
        )
        return
      }

      const response = await API.AdminAPI.quests.updateInquiryCustomForm(
        questDetail._id,
        payload,
      )

      if (response?.data) {
        if (
          response.data.isButtonNameUpdated !== undefined ||
          response.data.isCustomFormUpdated !== undefined
        ) {
          if (response.data.data?.questions) {
            const questions = response.data.data.questions
            if (questions.length > 0 && questions[0].id) {
              setCustomFormId(questions[0].id.split('_')[0])
            }
          }
          if (response.data.data?.customFormId) {
            setCustomFormId(response.data.data.customFormId)
          }
        } else if (
          response.data.customFormId ||
          response.data._id ||
          response.data.id
        ) {
          setCustomFormId(
            response.data.customFormId || response.data._id || response.data.id,
          )
        }
      }

      setLastSavedFormData(updatedFormData)
      reportCompleteness(updatedFormData)
      if (!options.silent) {
        Notification.showNotification(
          'Express interest form customization updated successfully!',
          'success',
        )
      }
    } catch (e) {
      Error.showError(e)
    } finally {
      setIsFormSubmitting(false)
    }
  }

  const closeExtraEditor = () => {
    setEditingExtraIndex(null)
    setExtraDraft(emptyExtraQuestion())
  }

  useEffect(() => {
    if (editingExtraIndex === null) {
      return undefined
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeInlineEditorAfterFlush(closeExtraEditor)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [editingExtraIndex])

  if (isLoading) {
    return <ABSpinner />
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={() => {}}>
      {({ values, setFieldValue, dirty }) => {
        const lockedQuestions =
          values.expressInterestForm?.questions?.slice(
            0,
            NON_EDITABLE_QUESTION_COUNT,
          ) || []
        const extraQuestions =
          values.expressInterestForm?.questions?.slice(
            NON_EDITABLE_QUESTION_COUNT,
          ) || []
        const isAddingExtra = editingExtraIndex === 'new'

        valuesRef.current = values

        const persistForm = async (formData, options = {}) => {
          await runQueuedFormikSave({
            values: formData,
            valuesRef,
            save: async (latest) => {
              await updateFormDetails(latest, options)
            },
            setInitialValues,
          })
        }

        const handleButtonLabelBlur = () => {
          setButtonLabelShowError(true)
          window.setTimeout(async () => {
            const formik = formikRef.current
            if (!formik) return
            const errors = await formik.validateForm()
            if (errors?.expressInterestForm?.inquiryButtonName) {
              return
            }
            await persistForm(formik.values, { silent: true })
          }, 0)
        }

        const openEditExtra = (index) => {
          if (editingExtraIndex === index) {
            closeInlineEditorAfterFlush(closeExtraEditor)
            return
          }
          setEditingExtraIndex(index)
          setExtraDraft({ ...extraQuestions[index] })
        }

        const openAddExtra = () => {
          setEditingExtraIndex('new')
          setExtraDraft(emptyExtraQuestion())
        }

        const autosaveExtraDraft = async (draftValues) => {
          if (
            editingExtraIndex === 'new' &&
            !hasExtraQuestionContent(draftValues)
          ) {
            return
          }

          let nextExtra
          if (editingExtraIndex === 'new') {
            nextExtra = [...extraQuestions, draftValues]
            setEditingExtraIndex(nextExtra.length - 1)
          } else if (typeof editingExtraIndex === 'number') {
            nextExtra = [...extraQuestions]
            nextExtra[editingExtraIndex] = {
              ...nextExtra[editingExtraIndex],
              ...draftValues,
            }
          } else {
            return
          }

          const updatedFormData = {
            expressInterestForm: {
              ...values.expressInterestForm,
              questions: [...lockedQuestions, ...nextExtra],
            },
          }
          setExtraDraft(draftValues)
          setFieldValue(
            'expressInterestForm.questions',
            updatedFormData.expressInterestForm.questions,
          )
          await persistForm(updatedFormData, { silent: true })
        }

        const deleteExtra = async (index) => {
          const next = extraQuestions.filter((_, i) => i !== index)
          const updatedFormData = {
            expressInterestForm: {
              ...values.expressInterestForm,
              questions: [...lockedQuestions, ...next],
            },
          }
          setFieldValue(
            'expressInterestForm.questions',
            updatedFormData.expressInterestForm.questions,
          )
          if (editingExtraIndex === index) closeExtraEditor()
          await persistForm(updatedFormData, { silent: true })
        }

        const requestDeleteExtra = (index) => {
          const question = extraQuestions[index]
          setDeleteModal({
            show: true,
            index,
            label:
              question?.description?.trim() ||
              question?.question?.trim() ||
              'Untitled question',
          })
          deleteActionRef.current = () => deleteExtra(index)
        }

        const closeDeleteModal = () => {
          setDeleteModal({ show: false, index: null, label: '' })
          deleteActionRef.current = null
        }

        const confirmDeleteExtra = async () => {
          const run = deleteActionRef.current
          closeDeleteModal()
          if (run) await run()
        }

        const deleteName = truncateLabel(deleteModal.label)
        const deleteMessage = deleteName
          ? `\u201C${deleteName}\u201D will be removed. It will no longer appear on Express Interest.`
          : 'It will no longer appear on Express Interest.'

        const moveExtraQuestion = (dragIndex, hoverIndex) => {
          const next = [...extraQuestions]
          const [moved] = next.splice(dragIndex, 1)
          next.splice(hoverIndex, 0, moved)
          const updatedQuestions = [...lockedQuestions, ...next]
          pendingReorderRef.current = {
            expressInterestForm: {
              ...values.expressInterestForm,
              questions: updatedQuestions,
            },
          }
          setFieldValue('expressInterestForm.questions', updatedQuestions)
          if (typeof editingExtraIndex === 'number') {
            if (editingExtraIndex === dragIndex) {
              setEditingExtraIndex(hoverIndex)
            } else if (
              dragIndex < editingExtraIndex &&
              hoverIndex >= editingExtraIndex
            ) {
              setEditingExtraIndex(editingExtraIndex - 1)
            } else if (
              dragIndex > editingExtraIndex &&
              hoverIndex <= editingExtraIndex
            ) {
              setEditingExtraIndex(editingExtraIndex + 1)
            }
          }
        }

        const persistAfterReorder = async () => {
          const payload = pendingReorderRef.current || formikRef.current?.values
          pendingReorderRef.current = null
          if (!payload) return
          await persistForm(payload, { silent: true })
        }

        return (
          <Form>
            <FormikDirtyReporter id='content-interest' dirty={dirty} />
            <div className='experience-f-row'>
              <div className='experience-f'>
                <ExperienceInputField
                  name='expressInterestForm.inquiryButtonName'
                  label='Button label'
                  placeholder='Express Interest'
                  capitalizeFirstLetter
                  formGroupProps={{ className: 'mb-0' }}
                  showErrorMessage={buttonLabelShowError}
                  displayFlex={false}
                  onBlur={handleButtonLabelBlur}
                />
              </div>
            </div>

            <div className='experience-field-head'>
              <h4>Always collected</h4>
              <span className='meta'>Locked</span>
            </div>
            <div className='experience-lrow-list'>
              {LOCKED_DISPLAY.map((meta, index) => (
                <div
                  key={meta.uiOnly ? 'occupancy' : index}
                  className='experience-lrow is-locked'>
                  <span className='experience-lrow-copy'>
                    <b>{meta.title}</b>
                    <small>{meta.hint}</small>
                  </span>
                  <span
                    className={`experience-req${meta.required ? ' on' : ''}`}>
                    {meta.required ? 'Required' : 'Optional'}
                  </span>
                </div>
              ))}
            </div>

            <div className='experience-field-head'>
              <h4>Extra questions</h4>
              <span className='meta'>
                {extraQuestions.length} of {MAX_EXTRA_QUESTIONS}
              </span>
            </div>
            <div className='experience-lrow-list' data-add-list='interest'>
              {extraQuestions.map((question, index) => (
                <Fragment key={question.id || question._id || `extra-${index}`}>
                  <InterestQuestionRow
                    question={question}
                    index={index}
                    isEditing={editingExtraIndex === index}
                    onEdit={openEditExtra}
                    onDelete={requestDeleteExtra}
                    moveItem={moveExtraQuestion}
                    onDragEnd={persistAfterReorder}
                  />
                  {editingExtraIndex === index ? (
                    <ExtraQuestionInlineEditor
                      initial={extraDraft}
                      onAutosave={autosaveExtraDraft}
                    />
                  ) : null}
                </Fragment>
              ))}
            </div>

            {isAddingExtra ? (
              <ExtraQuestionInlineEditor
                initial={extraDraft}
                onAutosave={autosaveExtraDraft}
              />
            ) : null}

            {editingExtraIndex === null &&
            extraQuestions.length < MAX_EXTRA_QUESTIONS ? (
              <div className='experience-form-actions'>
                <ExperienceButton
                  variant='outline'
                  size='sm'
                  onClick={openAddExtra}>
                  Add question
                </ExperienceButton>
              </div>
            ) : null}

            <p className='experience-f-hint text-dark mt-12px'>
              Submissions appear in Manage → Inquiries. This does not enroll
              them.
            </p>

            <ExperienceConfirmModal
              show={deleteModal.show}
              onHide={closeDeleteModal}
              onConfirm={confirmDeleteExtra}
              title='Delete this extra question?'
              message={deleteMessage}
              confirmLabel='Delete'
              isLoading={isFormSubmitting}
            />
          </Form>
        )
      }}
    </Formik>
  )
}

export default ExpressInterestForm
